import * as React from "react";

import { cn } from "../../lib/utils";

// Radio group built on native <input type="radio"> — no extra dependency
// (@radix-ui/react-radio-group is not installed). Brand color comes from the
// CSS `accent-color` (accent-coronis-blue-600), which resolves to the design
// system's `--ch-blue-600`.

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  name: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, name, value, defaultValue, onValueChange, disabled, children, ...props }, ref) => {
    const [internal, setInternal] = React.useState(defaultValue);
    const current = value !== undefined ? value : internal;
    const handle = (next: string) => {
      if (value === undefined) setInternal(next);
      onValueChange?.(next);
    };
    return (
      <RadioGroupContext.Provider value={{ name, value: current, onValueChange: handle, disabled }}>
        <div ref={ref} role="radiogroup" className={cn("flex flex-col gap-2", className)} {...props}>
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  },
);
RadioGroup.displayName = "RadioGroup";

export interface RadioGroupItemProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "name" | "onChange"> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, value, disabled, ...props }, ref) => {
    const ctx = React.useContext(RadioGroupContext);
    return (
      <input
        ref={ref}
        type="radio"
        name={ctx?.name}
        value={value}
        checked={ctx ? ctx.value === value : undefined}
        disabled={disabled ?? ctx?.disabled}
        onChange={() => ctx?.onValueChange?.(value)}
        className={cn(
          "h-[18px] w-[18px] shrink-0 cursor-pointer accent-coronis-blue-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-coronis-blue-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
